/**
 * ISO 4217 currency definitions.
 *
 * These track the latest ISO 4217 standard. DineroCurrency properties may change
 * when you upgrade Dinero.js. If you need stability, pin your package version.
 *
 * @see https://www.six-group.com/en/products-services/financial-information/data-standards.html
 */

import type { DineroCurrency } from './types';

/**
 * United Arab Emirates dirham.
 */
export const AED = {
  code: 'AED',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AED'>;

/**
 * Afghan afghani.
 */
export const AFN = {
  code: 'AFN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AFN'>;

/**
 * Albanian lek.
 */
export const ALL = {
  code: 'ALL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ALL'>;

/**
 * Armenian dram.
 */
export const AMD = {
  code: 'AMD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AMD'>;

/**
 * Angolan kwanza.
 */
export const AOA = {
  code: 'AOA',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AOA'>;

/**
 * Argentine peso.
 */
export const ARS = {
  code: 'ARS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ARS'>;

/**
 * Australian dollar.
 */
export const AUD = {
  code: 'AUD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AUD'>;

/**
 * Aruban florin.
 */
export const AWG = {
  code: 'AWG',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AWG'>;

/**
 * Azerbaijani manat.
 */
export const AZN = {
  code: 'AZN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'AZN'>;

/**
 * Bosnia and Herzegovina convertible mark.
 */
export const BAM = {
  code: 'BAM',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BAM'>;

/**
 * Barbados dollar.
 */
export const BBD = {
  code: 'BBD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BBD'>;

/**
 * Bangladeshi taka.
 */
export const BDT = {
  code: 'BDT',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BDT'>;

/**
 * Bulgarian lev.
 */
export const BGN = {
  code: 'BGN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BGN'>;

/**
 * Bahraini dinar.
 */
export const BHD = {
  code: 'BHD',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'BHD'>;

/**
 * Burundian franc.
 */
export const BIF = {
  code: 'BIF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'BIF'>;

/**
 * Bermudian dollar.
 */
export const BMD = {
  code: 'BMD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BMD'>;

/**
 * Brunei dollar.
 */
export const BND = {
  code: 'BND',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BND'>;

/**
 * Bolivian boliviano.
 */
export const BOB = {
  code: 'BOB',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BOB'>;

/**
 * Bolivian Mvdol.
 */
export const BOV = {
  code: 'BOV',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BOV'>;

/**
 * Brazilian real.
 */
export const BRL = {
  code: 'BRL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BRL'>;

/**
 * Bahamian dollar.
 */
export const BSD = {
  code: 'BSD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BSD'>;

/**
 * Bhutanese ngultrum.
 */
export const BTN = {
  code: 'BTN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BTN'>;

/**
 * Botswana pula.
 */
export const BWP = {
  code: 'BWP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BWP'>;

/**
 * Belarusian ruble.
 */
export const BYN = {
  code: 'BYN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BYN'>;

/**
 * Belize dollar.
 */
export const BZD = {
  code: 'BZD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'BZD'>;

/**
 * Canadian dollar.
 */
export const CAD = {
  code: 'CAD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CAD'>;

/**
 * Congolese franc.
 */
export const CDF = {
  code: 'CDF',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CDF'>;

/**
 * WIR Euro.
 */
export const CHE = {
  code: 'CHE',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CHE'>;

/**
 * Swiss franc.
 */
export const CHF = {
  code: 'CHF',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CHF'>;

/**
 * WIR Franc.
 */
export const CHW = {
  code: 'CHW',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CHW'>;

/**
 * Unidad de Fomento.
 */
export const CLF = {
  code: 'CLF',
  base: 10,
  exponent: 4,
} as const satisfies DineroCurrency<number, 'CLF'>;

/**
 * Chilean peso.
 */
export const CLP = {
  code: 'CLP',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'CLP'>;

/**
 * Renminbi (Chinese) yuan.
 */
export const CNY = {
  code: 'CNY',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CNY'>;

/**
 * Colombian peso.
 */
export const COP = {
  code: 'COP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'COP'>;

/**
 * Unidad de Valor Real.
 */
export const COU = {
  code: 'COU',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'COU'>;

/**
 * Costa Rican colón.
 */
export const CRC = {
  code: 'CRC',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CRC'>;

/**
 * Cuban peso.
 */
export const CUP = {
  code: 'CUP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CUP'>;

/**
 * Cape Verdean escudo.
 */
export const CVE = {
  code: 'CVE',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CVE'>;

/**
 * Czech koruna.
 */
export const CZK = {
  code: 'CZK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'CZK'>;

/**
 * Djiboutian franc.
 */
export const DJF = {
  code: 'DJF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'DJF'>;

/**
 * Danish krone.
 */
export const DKK = {
  code: 'DKK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'DKK'>;

/**
 * Dominican peso.
 */
export const DOP = {
  code: 'DOP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'DOP'>;

/**
 * Algerian dinar.
 */
export const DZD = {
  code: 'DZD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'DZD'>;

/**
 * Egyptian pound.
 */
export const EGP = {
  code: 'EGP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'EGP'>;

/**
 * Eritrean nakfa.
 */
export const ERN = {
  code: 'ERN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ERN'>;

/**
 * Ethiopian birr.
 */
export const ETB = {
  code: 'ETB',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ETB'>;

/**
 * Euro.
 */
export const EUR = {
  code: 'EUR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'EUR'>;

/**
 * Fiji dollar.
 */
export const FJD = {
  code: 'FJD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'FJD'>;

/**
 * Falkland Islands pound.
 */
export const FKP = {
  code: 'FKP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'FKP'>;

/**
 * Pound sterling.
 */
export const GBP = {
  code: 'GBP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GBP'>;

/**
 * Georgian lari.
 */
export const GEL = {
  code: 'GEL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GEL'>;

/**
 * Ghanaian cedi.
 */
export const GHS = {
  code: 'GHS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GHS'>;

/**
 * Gibraltar pound.
 */
export const GIP = {
  code: 'GIP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GIP'>;

/**
 * Gambian dalasi.
 */
export const GMD = {
  code: 'GMD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GMD'>;

/**
 * Guinean franc.
 */
export const GNF = {
  code: 'GNF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'GNF'>;

/**
 * Guatemalan quetzal.
 */
export const GTQ = {
  code: 'GTQ',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GTQ'>;

/**
 * Guyanese dollar.
 */
export const GYD = {
  code: 'GYD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'GYD'>;

/**
 * Hong Kong dollar.
 */
export const HKD = {
  code: 'HKD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'HKD'>;

/**
 * Honduran lempira.
 */
export const HNL = {
  code: 'HNL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'HNL'>;

/**
 * Haitian gourde.
 */
export const HTG = {
  code: 'HTG',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'HTG'>;

/**
 * Hungarian forint.
 */
export const HUF = {
  code: 'HUF',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'HUF'>;

/**
 * Indonesian rupiah.
 */
export const IDR = {
  code: 'IDR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'IDR'>;

/**
 * Israeli new shekel.
 */
export const ILS = {
  code: 'ILS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ILS'>;

/**
 * Indian rupee.
 */
export const INR = {
  code: 'INR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'INR'>;

/**
 * Iraqi dinar.
 */
export const IQD = {
  code: 'IQD',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'IQD'>;

/**
 * Iranian rial.
 */
export const IRR = {
  code: 'IRR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'IRR'>;

/**
 * Icelandic króna.
 */
export const ISK = {
  code: 'ISK',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'ISK'>;

/**
 * Jamaican dollar.
 */
export const JMD = {
  code: 'JMD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'JMD'>;

/**
 * Jordanian dinar.
 */
export const JOD = {
  code: 'JOD',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'JOD'>;

/**
 * Japanese yen.
 */
export const JPY = {
  code: 'JPY',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'JPY'>;

/**
 * Kenyan shilling.
 */
export const KES = {
  code: 'KES',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KES'>;

/**
 * Kyrgyzstani som.
 */
export const KGS = {
  code: 'KGS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KGS'>;

/**
 * Cambodian riel.
 */
export const KHR = {
  code: 'KHR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KHR'>;

/**
 * Comoro franc.
 */
export const KMF = {
  code: 'KMF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'KMF'>;

/**
 * North Korean won.
 */
export const KPW = {
  code: 'KPW',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KPW'>;

/**
 * South Korean won.
 */
export const KRW = {
  code: 'KRW',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'KRW'>;

/**
 * Kuwaiti dinar.
 */
export const KWD = {
  code: 'KWD',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'KWD'>;

/**
 * Cayman Islands dollar.
 */
export const KYD = {
  code: 'KYD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KYD'>;

/**
 * Kazakhstani tenge.
 */
export const KZT = {
  code: 'KZT',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'KZT'>;

/**
 * Lao kip.
 */
export const LAK = {
  code: 'LAK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'LAK'>;

/**
 * Lebanese pound.
 */
export const LBP = {
  code: 'LBP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'LBP'>;

/**
 * Sri Lankan rupee.
 */
export const LKR = {
  code: 'LKR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'LKR'>;

/**
 * Liberian dollar.
 */
export const LRD = {
  code: 'LRD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'LRD'>;

/**
 * Lesotho loti.
 */
export const LSL = {
  code: 'LSL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'LSL'>;

/**
 * Libyan dinar.
 */
export const LYD = {
  code: 'LYD',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'LYD'>;

/**
 * Moroccan dirham.
 */
export const MAD = {
  code: 'MAD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MAD'>;

/**
 * Moldovan leu.
 */
export const MDL = {
  code: 'MDL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MDL'>;

/**
 * Malagasy ariary.
 */
export const MGA = {
  code: 'MGA',
  base: 5,
  exponent: 1,
} as const satisfies DineroCurrency<number, 'MGA'>;

/**
 * Macedonian denar.
 */
export const MKD = {
  code: 'MKD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MKD'>;

/**
 * Myanmar kyat.
 */
export const MMK = {
  code: 'MMK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MMK'>;

/**
 * Mongolian tögrög.
 */
export const MNT = {
  code: 'MNT',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MNT'>;

/**
 * Macanese pataca.
 */
export const MOP = {
  code: 'MOP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MOP'>;

/**
 * Mauritanian ouguiya.
 */
export const MRU = {
  code: 'MRU',
  base: 5,
  exponent: 1,
} as const satisfies DineroCurrency<number, 'MRU'>;

/**
 * Mauritian rupee.
 */
export const MUR = {
  code: 'MUR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MUR'>;

/**
 * Maldivian rufiyaa.
 */
export const MVR = {
  code: 'MVR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MVR'>;

/**
 * Malawian kwacha.
 */
export const MWK = {
  code: 'MWK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MWK'>;

/**
 * Mexican peso.
 */
export const MXN = {
  code: 'MXN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MXN'>;

/**
 * Mexican Unidad de Inversion.
 */
export const MXV = {
  code: 'MXV',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MXV'>;

/**
 * Malaysian ringgit.
 */
export const MYR = {
  code: 'MYR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MYR'>;

/**
 * Mozambican metical.
 */
export const MZN = {
  code: 'MZN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'MZN'>;

/**
 * Namibian dollar.
 */
export const NAD = {
  code: 'NAD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NAD'>;

/**
 * Nigerian naira.
 */
export const NGN = {
  code: 'NGN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NGN'>;

/**
 * Nicaraguan córdoba.
 */
export const NIO = {
  code: 'NIO',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NIO'>;

/**
 * Norwegian krone.
 */
export const NOK = {
  code: 'NOK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NOK'>;

/**
 * Nepalese rupee.
 */
export const NPR = {
  code: 'NPR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NPR'>;

/**
 * New Zealand dollar.
 */
export const NZD = {
  code: 'NZD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'NZD'>;

/**
 * Omani rial.
 */
export const OMR = {
  code: 'OMR',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'OMR'>;

/**
 * Panamanian balboa.
 */
export const PAB = {
  code: 'PAB',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PAB'>;

/**
 * Peruvian sol.
 */
export const PEN = {
  code: 'PEN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PEN'>;

/**
 * Papua New Guinean kina.
 */
export const PGK = {
  code: 'PGK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PGK'>;

/**
 * Philippine peso.
 */
export const PHP = {
  code: 'PHP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PHP'>;

/**
 * Pakistani rupee.
 */
export const PKR = {
  code: 'PKR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PKR'>;

/**
 * Polish złoty.
 */
export const PLN = {
  code: 'PLN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'PLN'>;

/**
 * Paraguayan guaraní.
 */
export const PYG = {
  code: 'PYG',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'PYG'>;

/**
 * Qatari riyal.
 */
export const QAR = {
  code: 'QAR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'QAR'>;

/**
 * Romanian leu.
 */
export const RON = {
  code: 'RON',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'RON'>;

/**
 * Serbian dinar.
 */
export const RSD = {
  code: 'RSD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'RSD'>;

/**
 * Russian ruble.
 */
export const RUB = {
  code: 'RUB',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'RUB'>;

/**
 * Rwandan franc.
 */
export const RWF = {
  code: 'RWF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'RWF'>;

/**
 * Saudi riyal.
 */
export const SAR = {
  code: 'SAR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SAR'>;

/**
 * Solomon Islands dollar.
 */
export const SBD = {
  code: 'SBD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SBD'>;

/**
 * Seychelles rupee.
 */
export const SCR = {
  code: 'SCR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SCR'>;

/**
 * Sudanese pound.
 */
export const SDG = {
  code: 'SDG',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SDG'>;

/**
 * Swedish krona.
 */
export const SEK = {
  code: 'SEK',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SEK'>;

/**
 * Singapore dollar.
 */
export const SGD = {
  code: 'SGD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SGD'>;

/**
 * Saint Helena pound.
 */
export const SHP = {
  code: 'SHP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SHP'>;

/**
 * Sierra Leonean leone.
 */
export const SLE = {
  code: 'SLE',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SLE'>;

/**
 * Somali shilling.
 */
export const SOS = {
  code: 'SOS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SOS'>;

/**
 * Surinamese dollar.
 */
export const SRD = {
  code: 'SRD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SRD'>;

/**
 * South Sudanese pound.
 */
export const SSP = {
  code: 'SSP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SSP'>;

/**
 * São Tomé and Príncipe dobra.
 */
export const STN = {
  code: 'STN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'STN'>;

/**
 * Salvadoran colón.
 */
export const SVC = {
  code: 'SVC',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SVC'>;

/**
 * Syrian pound.
 */
export const SYP = {
  code: 'SYP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SYP'>;

/**
 * Swazi lilangeni.
 */
export const SZL = {
  code: 'SZL',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'SZL'>;

/**
 * Thai baht.
 */
export const THB = {
  code: 'THB',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'THB'>;

/**
 * Tajikistani somoni.
 */
export const TJS = {
  code: 'TJS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TJS'>;

/**
 * Turkmenistan manat.
 */
export const TMT = {
  code: 'TMT',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TMT'>;

/**
 * Tunisian dinar.
 */
export const TND = {
  code: 'TND',
  base: 10,
  exponent: 3,
} as const satisfies DineroCurrency<number, 'TND'>;

/**
 * Tongan paʻanga.
 */
export const TOP = {
  code: 'TOP',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TOP'>;

/**
 * Turkish lira.
 */
export const TRY = {
  code: 'TRY',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TRY'>;

/**
 * Trinidad and Tobago dollar.
 */
export const TTD = {
  code: 'TTD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TTD'>;

/**
 * New Taiwan dollar.
 */
export const TWD = {
  code: 'TWD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TWD'>;

/**
 * Tanzanian shilling.
 */
export const TZS = {
  code: 'TZS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'TZS'>;

/**
 * Ukrainian hryvnia.
 */
export const UAH = {
  code: 'UAH',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'UAH'>;

/**
 * Ugandan shilling.
 */
export const UGX = {
  code: 'UGX',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'UGX'>;

/**
 * United States dollar.
 */
export const USD = {
  code: 'USD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'USD'>;

/**
 * United States dollar (next day).
 */
export const USN = {
  code: 'USN',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'USN'>;

/**
 * Uruguay Peso en Unidades Indexadas.
 */
export const UYI = {
  code: 'UYI',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'UYI'>;

/**
 * Uruguayan peso.
 */
export const UYU = {
  code: 'UYU',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'UYU'>;

/**
 * Unidad previsional.
 */
export const UYW = {
  code: 'UYW',
  base: 10,
  exponent: 4,
} as const satisfies DineroCurrency<number, 'UYW'>;

/**
 * Uzbekistani soʻm.
 */
export const UZS = {
  code: 'UZS',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'UZS'>;

/**
 * Venezuelan digital bolívar.
 */
export const VED = {
  code: 'VED',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'VED'>;

/**
 * Venezuelan bolívar.
 */
export const VES = {
  code: 'VES',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'VES'>;

/**
 * Vietnamese đồng.
 */
export const VND = {
  code: 'VND',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'VND'>;

/**
 * Vanuatu vatu.
 */
export const VUV = {
  code: 'VUV',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'VUV'>;

/**
 * Samoan tālā.
 */
export const WST = {
  code: 'WST',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'WST'>;

/**
 * Arab Accounting Dinar.
 */
export const XAD = {
  code: 'XAD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'XAD'>;

/**
 * Central African CFA franc.
 */
export const XAF = {
  code: 'XAF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'XAF'>;

/**
 * East Caribbean dollar.
 */
export const XCD = {
  code: 'XCD',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'XCD'>;

/**
 * Caribbean guilder.
 */
export const XCG = {
  code: 'XCG',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'XCG'>;

/**
 * West African CFA franc.
 */
export const XOF = {
  code: 'XOF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'XOF'>;

/**
 * CFP franc.
 */
export const XPF = {
  code: 'XPF',
  base: 10,
  exponent: 0,
} as const satisfies DineroCurrency<number, 'XPF'>;

/**
 * Yemeni rial.
 */
export const YER = {
  code: 'YER',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'YER'>;

/**
 * South African rand.
 */
export const ZAR = {
  code: 'ZAR',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ZAR'>;

/**
 * Zambian kwacha.
 */
export const ZMW = {
  code: 'ZMW',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ZMW'>;

/**
 * Zimbabwe Gold.
 */
export const ZWG = {
  code: 'ZWG',
  base: 10,
  exponent: 2,
} as const satisfies DineroCurrency<number, 'ZWG'>;
